import EnhanceText from 'components/Dialogue/components/Message/EnhanceText';
import { useMemo } from 'react';
import { getFailCauseMsg } from 'hooks/business/conversation';
import { useTranslate } from '@tolgee/react';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import sysLangToTranslationLang from 'utils/sysLangToTranslationLang';
import dayjs from 'lib/dayjs';
import { get } from 'lodash-es';
import { cx } from '@emotion/css';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useGetMessageById } from 'hooks/business/message';

interface LastMessageProps {
  conversation: Conversation.Conversation;
  className?: HTMLSpanElement['className'];
}

export const LastMessage = (props: LastMessageProps) => {
  const { conversation, className } = props;
  const { t } = useTranslate();
  const sysLang = useSystemLanguageValue();
  const getMessageById = useGetMessageById();

  const {
    channel,
    lastMsgId,
    failCause,
    lastMsgType,
    lastMsgContent,
    lastMsgTime,
    localExtra,
    controlStatus,
    supervisePower,
  } = conversation;

  const tipTxt = useMemo(() => {
    const msg = getMessageById(lastMsgId);
    const status = msg?.status;
    const fromUserName = msg?.fromUserName ?? '';
    if (status === 'RECALL') {
      return t('messageType.recall');
    } else if (failCause) {
      return getFailCauseMsg(sysLang, failCause);
    } else {
      if (lastMsgType === 'SYS_BLACKLIST') {
        try {
          const transKey = sysLangToTranslationLang(sysLang);
          const json: Conversation.MsgText = JSON.parse(lastMsgContent);
          // @ts-ignore
          const str = (
            json.translationContent?.[transKey] || json.translationContent.enUS
          )?.text.replace(
            // eslint-disable-next-line no-template-curly-in-string
            '${time}',
            dayjs(Number(lastMsgTime)).format('YYYY-MM-DD HH:mm:ss'),
          );
          return `[${str}]`;
        } catch (error) {
          //
        }
      }

      if (lastMsgType === 'FACEBOOK_MESSAGE') {
        const {
          message,
          sticker,
          attachments: { data: [{ fileUrl, imageData, videoData } = {}] = [] } = {},
        } = msg?.fbMsgContent || {};
        if (message) {
          return message;
        }
        if (sticker) {
          return `[${t('messageType.STICKER')}]`;
        }
        if (imageData?.url) {
          return `[${t('messageType.IMAGE')}]`;
        }
        if (videoData?.url) {
          return `[${t('messageType.VIDEO')}]`;
        }
        if (fileUrl) {
          return `[${t('文件')}]`;
        }
        return lastMsgContent;
      }

      if (localExtra) {
        const { lastMsgId: localLastMsgId, lastMsgContent: localLastMsgContent } = localExtra;
        if (
          lastMsgType !== 'TEXT' &&
          lastMsgType !== 'ATTACHMENT' &&
          lastMsgType !== 'IMAGE_WITH_TEXT' &&
          localLastMsgId === lastMsgId &&
          localLastMsgContent
        ) {
          return `[${
            lastMsgType === 'SYS_TRANSFER' && controlStatus === true && !supervisePower
              ? t('messageType.SYS_ASSIGN')
              : t(`messageType.${lastMsgType}`)
          }]${localLastMsgContent}`;
        }
      }

      switch (lastMsgType) {
        case 'SYS_INIT_CONVERSATION': {
          return t('messageType.SYS_INIT_CONVERSATION', {
            who: fromUserName || '',
          });
        }
        case 'SYS_FAQ_QUESTION': {
          // 如果本地数据没有，则回退到lastMsgContent
          const text =
            get(msg, 'sysFaqQuestionContent')?.question || get(conversation, 'lastMsgContent');

          return t('messageType.SYS_FAQ_QUESTION', {
            text,
          });
        }
        case 'NOTIFICATION': {
          return msg?.notificationContent?.text ?? '';
        }
        case 'NONSUPPORT': {
          return isTiKTokUpperCase(msg?.channel)
            ? t('Entities.系统消息')
            : t('ViewsChat.nonsupported.label.of.platform', { platform: msg?.channel });
        }
        case 'SYS_USER_ENTER_FROM_TRANSFER':
        case 'SYS_ALLOCATED_SERVICE': {
          return t('Entities.系统消息');
        }
        case 'IMAGE_WITH_TEXT': {
          return `[${t('messageType.IMAGE')}]${lastMsgContent}`;
        }
        case 'TEXT':
        case 'ATTACHMENT': {
          return lastMsgContent;
        }
        case 'SYS_TRANSFER': {
          if (controlStatus === true && !supervisePower) {
            return `[${t('messageType.SYS_ASSIGN')}]${lastMsgContent}`;
          }
          break;
        }
        case 'FIRST_MSG_ITEM_RCMD': {
          return t('messageType.ITEM');
        }
        default: {
          return `[${t({
            key: `messageType.${lastMsgType}`,
            defaultValue: t('notification.UNKNOWN_MESSAGE_TYPE'),
          })}]`;
        }
      }
    }
  }, [
    getMessageById,
    lastMsgId,
    failCause,
    t,
    sysLang,
    lastMsgType,
    localExtra,
    lastMsgContent,
    lastMsgTime,
    controlStatus,
    supervisePower,
    conversation,
  ]);

  return (
    <span
      className={cx('text-ellipsis overflow-hidden whitespace-nowrap', className)}
      title={tipTxt}
    >
      <EnhanceText text={tipTxt} platform={channel} />
    </span>
  );
};
